<style scoped>

  @media only screen and (min-width: 991px) {
    .inline.fields {
      margin-top: -15px !important;
    }
  }

</style>

<template>
  
  <div>

    <div class="ui large breadcrumb">
      <a v-if="isCreating" class="clickable" @click="goBack()">
        <i class="reply icon m-r-5"></i> Laudos
      </a>
      <a v-else class="clickable" @click="$historyBack()">
        <i class="reply icon m-r-5"></i> Voltar
      </a>
      <i class="right chevron icon divider"></i>
      <div class="bold section">
        {{ isCreating ? creatingTitle : updatingTitle }}
      </div>
      <span v-if="isUpdating && !loading" class="animated fadeIn">
        <i class="right chevron icon divider"></i>
        <div class="section">
          <i class="info circle icon"></i> {{ cert.id }}
        </div>
        <i class="right chevron icon divider"></i>
        <div class="section">
          <cert-status :status="cert.status" />
        </div>
      </span>
    </div>

    <bookmark-button v-if="isCreating" :label="creatingTitle" />

    <div class="ui divider"></div>

    <div v-if="loading"><i class="spinner circle loading blue icon"></i> Carregando ...</div>

    <div v-show="!loading" class="animated fadeIn">

      <cert-data-segment ref="certDataSegment" @onChange="onChangeCertData" />      
      <exam-request-segment ref="examRequestSegment" @onChange="onChangeExamRequest" />
      <animal-segment ref="animalSegment" @onChange="onChangeAnimal" />
      <diagnosis-segment ref="diagnosisSegment" @onLoaded="initDiagnostics()" />  

      <div class="m-t-20">
        <button class="ui basic button" @click="$historyBack()">
          <i class="reply icon"></i> Cancelar
        </button>
        <button v-if="isUpdating && !cert.customerInvoiceId" class="ui basic red button m-l-10" :class="{ loading: removing }" 
          @click="$refs.confirmRemovalModal.show()">
          <i class="trash icon"></i> Excluir
        </button> 
        <button class="ui right floated blue button" :class="{ loading: saving }" @click="onConfirm()">
          <i class="check icon"></i> {{ isCreating ? 'Confirmar' : 'Atualizar' }}
        </button>         
      </div>      

    </div>      

    <yes-no-modal ref="confirmRemovalModal" danger="true" :message="`Confirma exclusão do laudo ${cert.id} ?`"  @onYes="onRemove()" />
    <yes-no-modal ref="finalityModal" :message="`${finalityMessage}. Deseja prosseguir ?`" @onYes="onConfirm({ checkFinality: false })" />

  </div>

</template> 

<script>

  import moment from "@/utils/moment"
  import CertService from "@/services/cert-service"
  import CustomerInvoiceService from "@/services/customer-invoice-service"
  import EmployeeInvoiceService from "@/services/employee-invoice-service"
  import CertStatus from "@/components/cert-status"
  import { CertDataSegment, ExamRequestSegment, AnimalSegment, DiagnosisSegment } from "./index"
  import { formPage, onlySimavet } from "@/mixins"  

  const certService = new CertService()
  const customerInvoiceService = new CustomerInvoiceService()
  const employeeInvoiceService = new EmployeeInvoiceService()

  export default {
    
    name: "CertForm",

    mixins: [formPage, onlySimavet],

    data() {
      return {
        finalityMessage: "",
        indexRoute: "/procs/certs",
        creatingTitle: "Novo Laudo",
        updatingTitle: "Alterar Laudo",        
        cert: {         
          status: "maintenance",
          type: "normal",
          finality: "unspecified",
          date: new Date(),
          user: this.$store.state.auth.user,
          userId: this.$store.state.auth.user.id,
          customerId: null,
          vetId: null,
          value: null,  
          examRequest: { user: {}, examType: {} },
        },
      }
    }, 
    
    components: {
      CertStatus,
      CertDataSegment,
      ExamRequestSegment,
      AnimalSegment,
      DiagnosisSegment,
    }, 

    mounted() {      
      this.loadCert()      
    },

    methods: {

      async loadCert() {

        if (!this.allowed)
          return

        this.loading = true

        try {

          if (this.isUpdating)
            this.cert = await certService.findById(this.$route.params.id)

          this.$refs.certDataSegment.init(this.cert)  
          this.$refs.examRequestSegment.init(this.cert)
          this.$refs.animalSegment.init(this.cert)     
          this.$refs.diagnosisSegment.init(this.cert)          

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.loading = false
        }
      },     
      
      onChangeCertData(data) {

        const { type, date, serviceId, serviceTypeId, user, customer, vet, finality } = data
        
        this.cert.date = date
        this.cert.serviceId = serviceId
        this.cert.serviceTypeId = serviceTypeId
        this.cert.user = user
        this.cert.userId = user ? user.id : null
        
        this.cert.customer = customer
        this.cert.customerId = customer ? customer.id : null
        
        this.cert.vet = vet
        this.cert.vetId = vet ? vet.id : null
        
        this.cert.finality = finality

        this.$refs.animalSegment.setCustomer(customer)

        this.refreshData()
      },

      onChangeExamRequest(data) {
        this.cert.examRequestId = data.examRequestId
        this.cert.examTypeId = data.examTypeId
        this.refreshData()
      },

      onChangeAnimal(data) {

        const { animalRegistration, animal } = data

        this.cert.animalRegistrationId = animalRegistration ? animalRegistration.id : null
        this.cert.animalId = animal ? animal.id : null
        this.cert.ownerId = animal ? animal.ownerId : null

        this.refreshData()
      },

      refreshData() {
        this.cert = this.$cloneObject(this.cert)
      },

      initDiagnostics() {

        if (this.isUpdating)
          this.$refs.diagnosisSegment.initDiagnostics(this.cert)                  
      },

      async onConfirm(options) {

        if (!this.validData())
          return

        this.saving = true

        const checkFinality = options ? options.checkFinality : true

        try {     
          
          if (checkFinality && !await this.checkFinality()) {
            this.saving = false
            return
          }

          this.cert.date = moment(this.cert.date).format("YYYY-MM-DD")

          if (!await this.checkDate()) {
            this.saving = false
            return
          }  

          if (this.isCreating) {
            const newCert = await certService.create(this.cert)
            this.cert.id = newCert.id
          } else {
            await certService.update(this.cert)
          }          

          this.$reloadDataTables(["exam-requests", "cert-complement-requests", "certs", "daily-certs"])

          this.$msg.success(this.isCreating ? "Laudo criado com sucesso" : "Laudo atualizado com sucesso")

          this.$router.push(`/procs/certs/${this.cert.id}/show`)

        } catch (error) {

          this.saving = false

          this.$errorHandler(error)
        }
      },

      validData() {

        let isOk = true
        let message = ""
        const { date, serviceId, serviceTypeId, userId, customerId, vetId, animalId } = this.cert

        if (!date) {
          message = "Informe a data do laudo"
        } else if (!serviceId) {
          message = "Informe o serviço do laudo"
        } else if (!serviceTypeId) {
          message = "Informe o tipo do serviço do laudo"  
        } else if (!userId) {
          message = "Informe o responsável pelo laudo"
        } else if (!customerId) {
          message = "Informe a clínica"  
        } else if (!vetId) {
          message = "Informe o veterinário"
        } else if (!animalId) {
          message = "Selecione um animal"
        }

        if (message) {
          isOk = false
          this.$msg.info(message)
        } else {
          isOk = this.validDiagnosis()
        }

        return isOk
      },

      validDiagnosis() {

        let isOk = true
        let message = ""
        const data = this.$refs.diagnosisSegment.getData()
        const { certTemplateId, template, diagnostics, comments, commentsContents, hasEmptyDiagnostics } = data

        if (!certTemplateId) {
          message = "Informe o modelo de laudo"
        } else if (template.organs.length > 0 && hasEmptyDiagnostics) {
          message = "Um ou mais órgãos não possuem diagnóstico"
        } else if (template.organs.length == 0 && !comments) {
          message = "Informe alguma observação sobre o laudo"
        }

        if (message) {
          isOk = false
          this.$msg.info(message)
        } else {
          this.cert.certTemplateId = certTemplateId
          this.cert.diagnostics = diagnostics
          this.cert.comments = comments
          this.cert.commentsContents = commentsContents
        }

        return isOk
      },

      async checkFinality() {
        
        const { serviceId, finality, animalRegistrationId, date } = this.cert
        const certId = this.isUpdating ? this.cert.id : null
        const data = await certService.checkFinality(serviceId, finality, animalRegistrationId, date, certId)
        const hasAlert = data.alert
        const isOk = !hasAlert

        if (hasAlert) {
          this.finalityMessage = data.message
          this.$refs.finalityModal.show()
        }  

        return isOk
      },

      async checkDate() {

        if (this.cert.customerInvoiceId || this.cert.employeeInvoiceId || this.$isAdmin())
          return true

        if (await customerInvoiceService.hasInvoice(this.cert.customerId, this.cert.date)) {
          this.$msg.warn("Já existe um fechamento para esse cliente referente à data informada")
          return false
        }

        if (await employeeInvoiceService.hasInvoice(this.cert.userId, this.cert.date)) {
          this.$msg.warn("Já existe um fechamento para esse colaborador referente à data informada")
          return false
        }

        return true
      },

      async onRemove() {
        
        this.removing = true

        try {

          await certService.remove(this.cert)
          await certService.removeAllFiles(this.cert.id)

          this.$reloadDataTables(["exam-requests", "cert-complement-requests", "certs", "daily-certs"])

          this.$msg.success("Laudo excluído com sucesso")

          this.$router.push("/procs/certs")

        } catch (error) {

          this.removing = false

          this.$errorHandler(error)
        }
      },
    },
  }

</script>